import type {
  StorageSiteMapFeature,
  StorageSiteMapFeatureCollection,
  StorageSiteMapFeatureType,
} from '../types/storage-site-map.ts';

export const STORAGE_SITE_MAP_MAX_FEATURES = 500;
export const STORAGE_SITE_MAP_MAX_LABEL_LENGTH = 200;
export const STORAGE_SITE_MAP_MAX_NOTE_LENGTH = 1_000;
export const STORAGE_SITE_MAP_MAX_COORDINATES_PER_SHAPE = 2_000;
export const STORAGE_SITE_MAP_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

const FEATURE_TYPES = new Set<StorageSiteMapFeatureType>([
  'central-accumulation-area',
  'satellite-accumulation-area',
  'permitted-storage-area',
  'other-storage-area',
  'pathway',
  'evacuation-route',
  'building-outline',
  'room',
  'entrance',
  'water-supply',
  'notification-system',
  'annotation',
]);

export const STORAGE_SITE_MAP_STORAGE_AREA_TYPES = new Set<StorageSiteMapFeatureType>([
  'central-accumulation-area',
  'satellite-accumulation-area',
  'permitted-storage-area',
  'other-storage-area',
]);

const EXPECTED_GEOMETRIES: Record<StorageSiteMapFeatureType, ReadonlySet<StorageSiteMapFeature['geometry']['type']>> = {
  'central-accumulation-area': new Set(['Point']),
  'satellite-accumulation-area': new Set(['Point']),
  'permitted-storage-area': new Set(['Point']),
  'other-storage-area': new Set(['Point']),
  pathway: new Set(['LineString']),
  'evacuation-route': new Set(['LineString']),
  'building-outline': new Set(['Polygon']),
  room: new Set(['Polygon']),
  entrance: new Set(['Point']),
  'water-supply': new Set(['Point']),
  'notification-system': new Set(['Point']),
  annotation: new Set(['Point', 'Polygon']),
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCoordinate(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'number' &&
    Number.isFinite(value[0]) &&
    value[0] >= -180 &&
    value[0] <= 180 &&
    typeof value[1] === 'number' &&
    Number.isFinite(value[1]) &&
    value[1] >= -90 &&
    value[1] <= 90
  );
}

function validateGeometry(
  value: unknown,
  path: string,
  errors: string[],
): StorageSiteMapFeature['geometry']['type'] | null {
  if (!isObject(value) || typeof value.type !== 'string') {
    errors.push(`${path} must be a supported GeoJSON geometry.`);
    return null;
  }
  if (value.type === 'Point') {
    if (!isCoordinate(value.coordinates)) errors.push(`${path}.coordinates must be a valid longitude/latitude pair.`);
    return 'Point';
  }
  if (value.type === 'LineString') {
    if (
      !Array.isArray(value.coordinates) ||
      value.coordinates.length < 2 ||
      value.coordinates.length > STORAGE_SITE_MAP_MAX_COORDINATES_PER_SHAPE ||
      !value.coordinates.every(isCoordinate)
    ) {
      errors.push(`${path}.coordinates must contain 2-${STORAGE_SITE_MAP_MAX_COORDINATES_PER_SHAPE} valid points.`);
    }
    return 'LineString';
  }
  if (value.type === 'Polygon') {
    const rings = value.coordinates;
    if (!Array.isArray(rings) || rings.length !== 1 || !Array.isArray(rings[0])) {
      errors.push(`${path}.coordinates must contain exactly one polygon ring.`);
      return 'Polygon';
    }
    const ring = rings[0];
    if (ring.length < 4 || ring.length > STORAGE_SITE_MAP_MAX_COORDINATES_PER_SHAPE || !ring.every(isCoordinate)) {
      errors.push(`${path}.coordinates must contain 4-${STORAGE_SITE_MAP_MAX_COORDINATES_PER_SHAPE} valid points.`);
    } else {
      const first = ring[0]!;
      const last = ring[ring.length - 1]!;
      if (first[0] !== last[0] || first[1] !== last[1]) errors.push(`${path} polygon ring must be closed.`);
    }
    return 'Polygon';
  }
  errors.push(`${path}.type is not supported.`);
  return null;
}

/**
 * Performs semantic validation beyond the transport schema: feature type and geometry must agree,
 * storage-area pins must link to a location, and all user-controlled strings and coordinates are bounded.
 */
export function storageSiteMapValidationErrors(value: unknown): string[] {
  const errors: string[] = [];
  if (!isObject(value) || value.type !== 'FeatureCollection' || !Array.isArray(value.features)) {
    return ['features must be a GeoJSON FeatureCollection.'];
  }
  if (value.features.length > STORAGE_SITE_MAP_MAX_FEATURES) {
    errors.push(`features cannot contain more than ${STORAGE_SITE_MAP_MAX_FEATURES} entries.`);
  }

  for (const [index, candidate] of value.features.entries()) {
    const path = `features[${index}]`;
    if (!isObject(candidate) || candidate.type !== 'Feature' || !isObject(candidate.properties)) {
      errors.push(`${path} must be a GeoJSON Feature with properties.`);
      continue;
    }
    const properties = candidate.properties;
    const featureType = properties.featureType;
    if (typeof featureType !== 'string' || !FEATURE_TYPES.has(featureType as StorageSiteMapFeatureType)) {
      errors.push(`${path}.properties.featureType is not supported.`);
      continue;
    }
    const typedFeatureType = featureType as StorageSiteMapFeatureType;
    const geometryType = validateGeometry(candidate.geometry, `${path}.geometry`, errors);
    if (geometryType && !EXPECTED_GEOMETRIES[typedFeatureType].has(geometryType)) {
      errors.push(`${path} cannot use ${geometryType} geometry for ${typedFeatureType}.`);
    }

    if (
      properties.label !== undefined &&
      (typeof properties.label !== 'string' || properties.label.length > STORAGE_SITE_MAP_MAX_LABEL_LENGTH)
    ) {
      errors.push(`${path}.properties.label must be at most ${STORAGE_SITE_MAP_MAX_LABEL_LENGTH} characters.`);
    }
    if (
      properties.note !== undefined &&
      (typeof properties.note !== 'string' || properties.note.length > STORAGE_SITE_MAP_MAX_NOTE_LENGTH)
    ) {
      errors.push(`${path}.properties.note must be at most ${STORAGE_SITE_MAP_MAX_NOTE_LENGTH} characters.`);
    }
    if (
      properties.color !== undefined &&
      (typeof properties.color !== 'string' || !STORAGE_SITE_MAP_COLOR_PATTERN.test(properties.color))
    ) {
      errors.push(`${path}.properties.color must be a six-digit hex color.`);
    }

    const locationID = properties.facilityStorageLocationID;
    if (STORAGE_SITE_MAP_STORAGE_AREA_TYPES.has(typedFeatureType)) {
      if (typeof locationID !== 'number' || !Number.isInteger(locationID) || locationID <= 0) {
        errors.push(`${path} storage-area pins must link to a storage location.`);
      }
    } else if (locationID !== undefined) {
      errors.push(`${path} cannot link a non-storage feature to a storage location.`);
    }
  }
  return errors;
}

export function assertValidStorageSiteMapFeatureCollection(
  value: unknown,
): asserts value is StorageSiteMapFeatureCollection {
  const errors = storageSiteMapValidationErrors(value);
  if (errors.length > 0) throw new Error(`Invalid site map: ${errors.slice(0, 5).join(' ')}`);
}
